import { useEffect, useMemo, useRef, useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { AlertTriangle, Lock, PencilLine, RefreshCw, Undo2 } from "lucide-react";
import { cn } from "@/utils/helpers";
import { useGetBrands } from "@/hooks/useGetBrands";
import { KitButton } from "../../components/kit";
import type { CalendarSlotIdea } from "@/types/contentCalendar.type";
import { PLAN_BRIEF_EDITABLE_STATUSES } from "@/types/contentCalendar.type";
import type {
  BriefComment,
  BriefRevisionEntry,
  BriefVisualPick,
  BriefVisualSource,
  CalendarBriefKind,
  ProductionBrief,
  ProductionBriefBody,
  ResolvedBriefVisual,
} from "@/types/productionBrief.type";
import {
  BRIEF_EDITABLE_STATUSES,
  BRIEF_STATUS_LABEL,
  emptyBodyFor,
  hasUnsentChanges,
  isStaticBody,
  liveBody,
} from "@/types/productionBrief.type";
import { useBriefRevisions } from "../api/useProductionBrief";
import { canFailBrief, minutesInState } from "../slotStage";
import { StuckRecoveryStrip } from "./StuckRecovery";
import ProductionBriefDoc, { ProductionBriefSkeleton } from "./ProductionBriefDoc";
import ProductionBriefEditor, { BriefVisualDialog, bodyWithRowVisuals } from "./ProductionBriefEditor";
import { resolvedFromChoice } from "./brief/briefVisualChoice";
import type { MediaChoice } from "../concepts/IdeaMediaDialog";
import BriefComments from "./BriefComments";

/** How a save is asked of the host: against which version, and whether it says so in a toast. */
export interface BriefSaveOptions {
  /** The version the body was written against; the host's own when omitted. */
  baseVersion?: number;
  /** A picture pick: the card filling is the confirmation, so no "Brief saved" toast. */
  quiet?: boolean;
}

/** A picture picked on the read view, shown until its save lands (null takes it off). */
interface PendingVisual {
  token: number;
  pick: BriefVisualPick | null;
  resolved: ResolvedBriefVisual | null;
}

/** The body as read, with the picks still on their way painted over their rows. */
function withPendingVisuals(
  kind: CalendarBriefKind,
  body: ProductionBriefBody,
  pending: ReadonlyMap<number, PendingVisual>,
): ProductionBriefBody {
  const paint = <T extends { visualSource?: BriefVisualSource; resolvedVisual?: ResolvedBriefVisual }>(rows: T[]) =>
    rows.map((row, index) => {
      const entry = pending.get(index);
      if (!entry) return row;
      const next = { ...row };
      if (entry.pick && entry.resolved) {
        next.visualSource = entry.resolved.source;
        next.resolvedVisual = entry.resolved;
      } else {
        delete next.visualSource;
        delete next.resolvedVisual;
      }
      return next;
    });
  return isStaticBody(kind, body)
    ? { ...body, panels: paint(body.panels) }
    : { ...body, storyboard: paint(body.storyboard) };
}

/** "Chose a picture for shot 2", "Chose pictures for shots 2 and 3", "Took the picture off slide 1". */
function visualChangeNote(kind: CalendarBriefKind, entries: ReadonlyMap<number, PendingVisual>): string {
  const noun = kind === "STATIC" ? "slide" : "shot";
  const numbers = [...entries.keys()].sort((a, b) => a - b).map((index) => String(index + 1));
  const list = numbers.length <= 1 ? (numbers[0] ?? "") : `${numbers.slice(0, -1).join(", ")} and ${numbers[numbers.length - 1]}`;
  const cleared = [...entries.values()].every((entry) => entry.pick === null);
  if (numbers.length === 1) return cleared ? `Took the picture off ${noun} ${list}` : `Chose a picture for ${noun} ${list}`;
  return cleared ? `Took the pictures off ${noun}s ${list}` : `Chose pictures for ${noun}s ${list}`;
}

type BriefDeclineDecision = NonNullable<BriefRevisionEntry["decisionInRound"]>;

/**
 * The decline still waiting on a rewrite — same role as ClientDeclineBanner on
 * the idea side. Prefer the verdict on the version last SENT (what they
 * rejected); fall back to the highest-round decline if that row is missing.
 */
function openBriefDecline(
  brief: ProductionBrief,
  revisions: BriefRevisionEntry[] | undefined,
): BriefDeclineDecision | null {
  if (brief.status !== "CHANGES_REQUESTED" || !revisions) return null;

  const declinesFor = (entry: BriefRevisionEntry): BriefDeclineDecision[] => {
    const all =
      entry.decisionsInRounds?.length
        ? entry.decisionsInRounds
        : entry.decisionInRound
          ? [entry.decisionInRound]
          : [];
    return all.filter((d) => d.decision === "DECLINED");
  };

  if (brief.sentVersion != null) {
    const onSent = revisions.find((r) => r.version === brief.sentVersion);
    if (onSent) {
      const declines = declinesFor(onSent);
      if (declines.length > 0) return declines[declines.length - 1]!;
    }
  }

  let best: BriefDeclineDecision | null = null;
  for (const entry of revisions) {
    for (const decision of declinesFor(entry)) {
      if (!best || decision.roundNumber > best.roundNumber) best = decision;
    }
  }
  return best;
}

function BriefDeclineBanner({ decision }: { decision: BriefDeclineDecision }) {
  return (
    <div className="flex flex-col gap-3 rounded-card bg-[color-mix(in_srgb,var(--co-attention)_10%,transparent)] p-4">
      <div className="flex items-start gap-2.5">
        <Undo2
          className="mt-0.5 size-4 shrink-0 text-[var(--co-attention)]"
          strokeWidth={2}
        />
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-co-text2">
            {decision.clientName} declined this brief
            {decision.roundNumber ? ` in round ${decision.roundNumber}` : ""}
          </p>
        </div>
      </div>

      {decision.reason ? (
        <blockquote className="border-l-2 border-[color-mix(in_srgb,var(--co-attention)_45%,transparent)] pl-3 text-[13.5px] leading-relaxed text-co-text2">
          {decision.reason}
        </blockquote>
      ) : (
        <p className="text-[12.5px] leading-relaxed text-co-muted">
          They gave no reason. Check the thread before rewriting.
        </p>
      )}
    </div>
  );
}

/**
 * The workroom's brief surface: the document, its history, and the thread.
 * The picked idea stays on the Concept tab.
 */
export default function ProductionBriefPanel({
  brief,
  brandId,
  comments = [],
  canEdit,
  planStatus,
  winner,
  pillarHue,
  onSave,
  saving,
  onComment,
  commentBusy = false,
  now,
  onForceFail,
  forceFailBusy = false,
  onRetry,
  retrying = false,
  editRequest = 0,
  bare = false,
}: {
  brief: ProductionBrief;
  /** Whose libraries the per-shot visual picker offers. */
  brandId: string;
  /** Legacy standalone workroom thread. The current review workspace owns its collapsible thread. */
  comments?: BriefComment[];
  canEdit: boolean;
  planStatus: string;
  /** The signed-off idea this brief executes. */
  winner: CalendarSlotIdea | null;
  /** Stable content-pillar hue shared with Calendar and Distribution. */
  pillarHue?: string;
  /** Save a new version; answers with the brief as saved (the next pick builds on it). */
  onSave: (body: ProductionBriefBody, changeNote: string | undefined, options?: BriefSaveOptions) => Promise<ProductionBrief>;
  saving: boolean;
  onComment?: (body: string) => void;
  commentBusy?: boolean;
  /** A ticking clock, when the caller wants a stranded GENERATING brief surfaced. */
  now?: number;
  /** Condemn a stranded brief so it can be hand-written. Omitted for read-only callers. */
  onForceFail?: () => void;
  forceFailBusy?: boolean;
  /** Have another go at a FAILED brief. Omitted for read-only callers. */
  onRetry?: () => void;
  retrying?: boolean;
  /** Bumped by the page's "Edit the brief" primary: each new value opens the editor. */
  editRequest?: number;
  /**
   * The team's post page: its status banner already says the client declined
   * and carries "Edit the brief", so the panel shows the brief alone (Pen EIrjn).
   */
  bare?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [seenEditRequest, setSeenEditRequest] = useState(0);
  if (editRequest !== seenEditRequest) {
    setSeenEditRequest(editRequest);
    if (editRequest > 0) setEditing(true);
  }
  const { data: brands } = useGetBrands();
  const brandName = brands?.find((b) => b.id === brandId)?.name ?? null;

  const body = liveBody(brief);
  const generating = brief.status === "GENERATING";
  const failed = brief.status === "FAILED";

  // The BRIEF's own status is the load-bearing gate: it locks an accepted brief
  // and one the client is currently reading, while every other brief in the
  // month stays freely editable. The plan gate only closes an archived month.
  const planAllows = (PLAN_BRIEF_EDITABLE_STATUSES as string[]).includes(planStatus);
  const briefAllows = (BRIEF_EDITABLE_STATUSES as string[]).includes(brief.status);
  const editable = canEdit && planAllows && briefAllows;

  // Fetch once the brief has been sent (or declined) — that is when decisions
  // and multi-version history exist. A lone declined v1 still needs the reason.
  const revisionsQuery = useBriefRevisions(
    brief.id,
    brief.version > 1 || !!brief.sentVersion || brief.status === "CHANGES_REQUESTED",
  );

  const decline = useMemo(
    () => openBriefDecline(brief, revisionsQuery.data?.revisions),
    [brief, revisionsQuery.data?.revisions],
  );

  // The read view's picture picks: which shot/slide the picker is open for, and
  // the picks painted on their cards until their saves land. Each pick is saved
  // as a new version, one save at a time: a pick made while one is saving waits
  // and goes out with any others on top of the version that save answers with
  // (the server's compare-and-swap would refuse two saves on the same version).
  const [pickingRow, setPickingRow] = useState<number | null>(null);
  const [pendingVisuals, setPendingVisuals] = useState<ReadonlyMap<number, PendingVisual>>(() => new Map());
  const queuedVisuals = useRef(new Map<number, PendingVisual>());
  const savingVisuals = useRef(false);
  const visualToken = useRef(0);
  const latest = useRef({ brief, onSave });
  useEffect(() => {
    latest.current = { brief, onSave };
  });
  const picksSaving = pendingVisuals.size > 0;

  const settleVisuals = (entries: ReadonlyMap<number, PendingVisual>) =>
    setPendingVisuals((current) => {
      const next = new Map(current);
      // A row picked again since keeps its newer pick.
      for (const [index, entry] of entries) if (next.get(index)?.token === entry.token) next.delete(index);
      return next;
    });

  const flushVisuals = async () => {
    if (savingVisuals.current) return;
    savingVisuals.current = true;
    let base = latest.current.brief;
    let batch = new Map<number, PendingVisual>();
    try {
      while (queuedVisuals.current.size > 0) {
        batch = new Map(queuedVisuals.current);
        queuedVisuals.current.clear();
        const baseBody = liveBody(base);
        if (!baseBody) throw new Error("The brief has no body to put a picture on.");
        const picks = new Map([...batch].map(([index, entry]) => [index, entry.pick] as const));
        base = await latest.current.onSave(
          bodyWithRowVisuals(base.kind, baseBody, picks),
          visualChangeNote(base.kind, batch),
          { baseVersion: base.version, quiet: true },
        );
        settleVisuals(batch);
      }
    } catch {
      // The save toasts its own error. Every pick not on the server comes off its card.
      const dropped = new Map([...batch, ...queuedVisuals.current]);
      queuedVisuals.current.clear();
      settleVisuals(dropped);
    } finally {
      savingVisuals.current = false;
    }
  };

  const pickVisual = (index: number, choice: MediaChoice | null) => {
    setPickingRow(null);
    if (!body) return;
    visualToken.current += 1;
    const entry: PendingVisual = {
      token: visualToken.current,
      pick: choice?.pick ?? null,
      resolved: choice ? resolvedFromChoice(choice) : null,
    };
    queuedVisuals.current.set(index, entry);
    setPendingVisuals((current) => new Map(current).set(index, entry));
    void flushVisuals();
  };

  const shownBody = useMemo(
    () => (body && pendingVisuals.size > 0 ? withPendingVisuals(brief.kind, body, pendingVisuals) : body),
    [body, brief.kind, pendingVisuals],
  );

  // DERIVED, not synced in an effect. If a colleague sends the month while this
  // form is open the editor has to close, and doing that by writing state from an
  // effect costs a cascading render for something that is simply a function of
  // the two values. It also waits out a picture pick still saving, so it opens
  // on the version that pick makes rather than one about to be replaced.
  const showEditor = editing && editable && !picksSaving;

  if (generating) {
    const stranded = now !== undefined && canFailBrief(brief, now) && !!onForceFail;
    return (
      <div className="flex flex-col gap-4">
        {stranded ? (
          <StuckRecoveryStrip
            copy={{
              title: "Not moving?",
              body: "Writing a brief normally takes a few minutes. If it has been much longer, a server restart probably dropped the run — marking it failed stops the wait, and from there you can write it yourself or hit Try again.",
              action: "Mark as failed",
              confirmTitle: "Stop waiting on this run?",
              confirmBody:
                "The brief moves to Failed, which is its editable state — from there you can write the storyboard, props, wardrobe and locations yourself, or press Try again to have another go at generating it.",
              // Nothing is restarted, so there is nothing to pay for twice.
              warnsDoubleSpend: false,
            }}
            minutes={minutesInState(brief.updatedAt, now)}
            busy={forceFailBusy}
            onConfirm={onForceFail}
          />
        ) : null}
        <ProductionBriefSkeleton />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {bare ? null : decline ? (
        <BriefDeclineBanner decision={decline} />
      ) : brief.status === "CHANGES_REQUESTED" && revisionsQuery.isLoading ? (
        <StatusBanner
          tone="warn"
          text="The client sent this back. Loading their reason…"
        />
      ) : (
        <BriefStatusLine brief={brief} planStatus={planStatus} />
      )}

      {failed && (
        <div className="flex flex-col gap-3 rounded-card bg-[color-mix(in_srgb,var(--co-attention)_10%,transparent)] p-4">
          <div className="flex items-start gap-2.5">
            <AlertTriangle
              className="mt-0.5 size-4 shrink-0 text-[var(--co-attention)]"
              strokeWidth={2}
            />
            <div className="min-w-0 leading-tight">
              <p className="text-[13px] font-medium text-co-text2">
                The brief could not be written automatically
              </p>
              {brief.generationError && (
                <p className="mt-1 break-words text-[12.5px] leading-relaxed text-co-muted">
                  {brief.generationError}
                </p>
              )}
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-co-muted">
                {onRetry
                  ? "Try again, or write it yourself below — nothing is lost either way, since this brief has no content yet."
                  : "Write it yourself below."}
              </p>
            </div>
          </div>
          {onRetry && (
            <KitButton
              variant="primary"
              iconLeft={RefreshCw}
              loading={retrying}
              onClick={onRetry}
              className="self-start"
            >
              Try again
            </KitButton>
          )}
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex min-w-0 flex-col gap-4">
          {/* The post page edits from its ⋯ menu ("Edit the brief"); a brief with
              nothing written yet still needs a way in, since the failed note
              above says to write it below. */}
          {editable && !showEditor && (!bare || !body) ? (
            <div className="flex justify-end">
              <KitButton
                variant="secondary"
                iconLeft={PencilLine}
                onClick={() => setEditing(true)}
                isDisabled={picksSaving}
              >
                {body ? "Edit brief" : "Write it"}
              </KitButton>
            </div>
          ) : null}

          {showEditor ? (
            <ProductionBriefEditor
              brief={brief}
              brandId={brandId}
              body={body ?? emptyBodyFor(brief.kind)}
              idea={winner}
              onSave={async (nextBody, changeNote) => {
                await onSave(nextBody, changeNote);
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
              saving={saving}
            />
          ) : shownBody ? (
            <ProductionBriefDoc
              kind={brief.kind}
              body={shownBody}
              showHeader={false}
              pillarHue={pillarHue}
              brandName={brandName}
              askedSeconds={winner?.durationSeconds}
              aspectRatio={winner?.aspectRatio}
              // Every card stays live while a pick saves: a pick queues behind it.
              onChooseVisual={editable ? setPickingRow : undefined}
            />
          ) : (
            <p className="text-[13.5px] leading-relaxed text-co-muted">
              Nothing written yet.
            </p>
          )}

          {pickingRow !== null && editable && shownBody ? (
            <BriefVisualDialog
              brandId={brandId}
              kind={brief.kind}
              body={shownBody}
              index={pickingRow}
              idea={winner}
              onClose={() => setPickingRow(null)}
              onConfirm={(choice) => pickVisual(pickingRow, choice)}
            />
          ) : null}

          {onComment ? (
            <BriefComments
              comments={comments}
              currentVersion={brief.version}
              onSubmit={onComment}
              submitting={commentBusy}
              readOnly={!canEdit}
            />
          ) : null}
        </div>

      </div>
    </div>
  );
}

/** One line saying where the brief stands and, when it matters, whose turn it is. */
function BriefStatusLine({ brief, planStatus }: { brief: ProductionBrief; planStatus: string }) {
  const unsent = hasUnsentChanges(brief);
  // The team's workflow banners; a guest has the status chip and their answer.
  const { isGuest } = usePermissions();
  if (isGuest) return null;

  if (brief.status === "ACCEPTED") return null;
  if (brief.status === "IN_REVIEW") {
    return (
      <StatusBanner
        tone="client"
        icon={<Lock className="size-3.5 shrink-0" strokeWidth={2} />}
        text="With the client. Nothing can change while they are reading it."
      />
    );
  }
  if (brief.status === "CHANGES_REQUESTED") {
    return (
      <StatusBanner
        tone="warn"
        text="The client sent this back. Rewrite it by hand, then re-send the month — no robot is going to do this one."
      />
    );
  }
  if (unsent) {
    return (
      <StatusBanner
        tone="warn"
        text="You have unsent edits — the client is still reading the last version you sent."
      />
    );
  }
  return (
    <p className="text-[12.5px] text-co-muted">
      {BRIEF_STATUS_LABEL[brief.status]}
      {planStatus === "BRIEFS_DRAFT" ? " · not sent yet" : ""}
    </p>
  );
}

function StatusBanner({
  tone,
  text,
  icon,
}: {
  tone: "machine" | "warn" | "done" | "client";
  text: string;
  icon?: React.ReactNode;
}) {
  const style =
    tone === "done"
      ? "bg-[var(--co-live)]/10"
      : tone === "warn"
        ? "bg-[var(--co-attention)]/10"
        : tone === "machine"
          ? "bg-co-accent-dim"
          : "bg-[var(--co-tonal)]";
  const dot =
    tone === "done"
      ? "bg-[var(--co-live)]"
      : tone === "warn"
        ? "bg-[var(--co-attention)]"
        : tone === "machine"
          ? "bg-co-accent"
          : "bg-co-faint";

  return (
    <div className={cn("flex items-center gap-2.5 rounded-card px-4 py-3", style)}>
      {icon ?? <span className={cn("size-2 shrink-0 rounded-full", dot)} />}
      <p className="text-[13.5px] leading-[1.4] text-co-text2">{text}</p>
    </div>
  );
}
